<template>
	<div ref="container" tabindex="-1" v-click-outside="() => close(false)" class="header-search" :class="{ 'is-expanded': expanded }" :style="{ '--search-expanded-width': `${expandedWidth}px` }" @keydown.esc.stop.prevent="close()">
		<div class="header-search-field">
			<v-icon size="20" aria-hidden="true">mdi-magnify</v-icon>
			<div class="search-input">
				<span ref="inputSizer" class="search-input-sizer" aria-hidden="true">{{ t('common.headerSearch.placeholder') }}</span>
			<input size="1" ref="input" v-model="query" role="combobox" aria-autocomplete="list"
				:aria-controls="expanded ? 'header-search-results' : undefined" :aria-expanded="expanded"
				:aria-activedescendant="activeIndex >= 0 ? `header-result-${activeIndex}` : undefined"
				:aria-label="t('common.headerSearch.open')" :placeholder="t(expanded ? 'common.headerSearch.placeholder' : 'common.headerSearch.collapsed')"
				autocomplete="off" @focus="open" @click="open" @keydown.down.prevent="move(1)" @keydown.up.prevent="move(-1)"
				@keydown.enter.prevent="selectActive" />
			</div>
			<v-btn v-if="expanded" icon="mdi-close" size="x-small" variant="text" :aria-label="t('common.close')" @click="close()" />
		</div>
		<div v-if="expanded" class="search-results">
			<div class="search-status" role="status" aria-live="polite">
				<span v-if="query.trim().length < 2">{{ t('common.headerSearch.hint') }}</span>
				<span v-else-if="loading">{{ t('common.loading') }}</span>
				<span v-else-if="failed">{{ t('common.headerSearch.error') }}</span>
				<span v-else-if="!results.length">{{ t('common.headerSearch.empty') }}</span>
				<span v-else>{{ t('common.headerSearch.limit') }}</span>
			</div>
			<div id="header-search-results" role="listbox" :aria-label="t('common.headerSearch.open')" :aria-busy="loading">
				<router-link v-for="(result, index) in results" :id="`header-result-${index}`"
					:key="`${result.kind}-${result.id}`" :to="result.path" role="option" :aria-selected="activeIndex === index"
					class="search-result" :class="{ 'is-active': activeIndex === index }" @click="close(false)">
					<v-avatar size="36" color="primary" class="search-avatar">
						<img v-if="result.photoUrl && !brokenPhotos.has(result.photoUrl)" :src="result.photoUrl" alt=""
							@error="brokenPhotos.add(result.photoUrl!)" />
						<v-icon v-else size="20">{{ result.kind === 'guide' ? 'mdi-star-four-points' : 'mdi-account' }}</v-icon>
					</v-avatar>
					<span class="result-content">
						<span class="result-name">{{ result.name }}</span>
						<span v-if="result.kind === 'guide'" class="result-medium">{{ result.primaryMedium
							? t('common.headerSearch.primaryMedium', { name: result.primaryMedium }) : t('common.headerSearch.noPrimary') }}</span>
					</span>
					<span class="result-kind">{{ t(`common.headerSearch.${result.kind}`) }}</span>
				</router-link>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { searchHeader, type HeaderSearchResult } from "@/services/headerSearchService";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const expanded = ref(false);
const query = ref("");
const input = ref<HTMLInputElement>();
const container = ref<HTMLElement>();
const inputSizer = ref<HTMLElement>();
const expandedWidth = ref(320);
const results = ref<HeaderSearchResult[]>([]);
const brokenPhotos = reactive(new Set<string>());
const loading = ref(false);
const failed = ref(false);
const activeIndex = ref(-1);
let timer: ReturnType<typeof setTimeout> | undefined;
let requestVersion = 0;
// Mede o texto sem depender da API de animações nem da largura em transição.
function measureExpandedWidth() {
	const textWidth = inputSizer.value?.getBoundingClientRect().width;
	if (!textWidth) return;
	// Ícone (20), fechar (32), gaps (16), padding (20) e bordas (2).
	expandedWidth.value = Math.ceil(textWidth + 90);
}
watch(() => t("common.headerSearch.placeholder"), () => nextTick(measureExpandedWidth));

async function open() {
	if (expanded.value) return;
	measureExpandedWidth();
	expanded.value = true;
	await nextTick();
	input.value?.focus();
}
function close(restoreFocus = true) {
	if (!expanded.value) return;
	expanded.value = false;
	query.value = "";
	reset();
	if (document.activeElement === input.value) input.value?.blur();
	if (restoreFocus) container.value?.focus();
}
function reset() {
	clearTimeout(timer);
	requestVersion++;
	results.value = [];
	activeIndex.value = -1;
	loading.value = false;
	failed.value = false;
}
watch(query, () => {
	reset();
	if (!expanded.value || query.value.trim().length < 2) return;
	loading.value = true;
	const version = requestVersion;
	timer = setTimeout(async () => {
		try {
			const response = await searchHeader(query.value);
			if (version !== requestVersion) return;
			results.value = response.results;
			failed.value = response.failed;
		} catch {
			if (version === requestVersion) failed.value = true;
		} finally {
			if (version === requestVersion) loading.value = false;
		}
	}, 300);
});
watch(() => route.fullPath, () => close(false));
function move(direction: number) {
	if (!results.value.length) return;
	activeIndex.value = activeIndex.value < 0
		? (direction > 0 ? 0 : results.value.length - 1)
		: (activeIndex.value + direction + results.value.length) % results.value.length;
	nextTick(() => document.getElementById(`header-result-${activeIndex.value}`)?.scrollIntoView({ block: "nearest" }));
}
function selectActive() {
	const result = results.value[activeIndex.value];
	if (!result) return;
	router.push(result.path);
	close();
}
function shortcut(event: KeyboardEvent) {
	if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
		event.preventDefault();
		expanded.value ? close() : open();
	}
}
onMounted(() => {
	measureExpandedWidth();
	window.addEventListener("keydown", shortcut);
});
onBeforeUnmount(() => {
	reset();
	window.removeEventListener("keydown", shortcut);
});
</script>

<style scoped>
.header-search { position: relative; flex-shrink: 0; width: 160px; margin-inline: 8px; transition: width 260ms cubic-bezier(0.2, 0, 0, 1); }
.header-search.is-expanded { width: var(--search-expanded-width); margin-inline: 8px; }
.header-search-field { display: flex; align-items: center; gap: 8px; min-height: 40px; padding: 4px 8px 4px 12px; border: 1px solid rgba(var(--v-theme-on-surface), .22); border-radius: 12px; background: rgb(var(--v-theme-surface)); }
.search-input { display: grid; min-width: 0; flex: 1; }
.search-input-sizer, .header-search-field input { grid-area: 1 / 1; font: inherit; font-size: .875rem; }
.search-input-sizer { position: absolute; visibility: hidden; white-space: pre; width: max-content; padding-right: 2px; pointer-events: none; }
.header-search-field input { min-width: 0; width: 100%; outline: none; font: inherit; font-size: .875rem; color: rgb(var(--v-theme-on-surface)); }
.is-expanded .header-search-field { border-color: rgb(var(--v-theme-primary)); }
.header-search-field:focus-within { box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), .18); }
.search-results { position: absolute; top: calc(100% + 10px); right: 0; width: max(100%, 380px); max-width: calc(100vw - 24px); max-height: min(65vh, 520px); overflow-y: auto; background: rgb(var(--v-theme-surface)); color: rgb(var(--v-theme-on-surface)); border: 1px solid rgba(var(--v-theme-on-surface), .12); border-radius: 12px; box-shadow: 0 8px 30px #0003; }
.search-status { padding: 12px 16px; font-size: .8rem; color: rgba(var(--v-theme-on-surface), .7); }
.search-result { display: flex; gap: 10px; align-items: center; padding: 12px; color: inherit; text-decoration: none; }
.search-result:hover, .search-result:focus-visible, .search-result.is-active { background: rgba(var(--v-theme-primary), .1); }
.search-avatar { flex-shrink: 0; }
.search-avatar img { width: 100%; height: 100%; object-fit: cover; }
.result-content { flex: 1; min-width: 0; }
.result-name, .result-medium { display: block; overflow-wrap: anywhere; }
.result-name { font-size: .875rem; font-weight: 500; }
.result-medium { font-size: .75rem; color: rgba(var(--v-theme-on-surface), .65); margin-top: 2px; }
.result-kind { font-size: .65rem; color: rgb(var(--v-theme-primary)); flex-shrink: 0; }
@media (max-width: 1100px) {
	.header-search { width: 120px; min-width: 88px; flex-shrink: 1; margin-inline: 8px; }
	.header-search.is-expanded { position: absolute; inset: 8px 8px auto auto; width: calc(100% - 16px); margin: 0; z-index: 2; }
	.search-results { width: 100%; max-width: none; }
}
@media (prefers-reduced-motion: reduce) { .header-search { transition: none; } }
</style>
